import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Result, handleServerErrors, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { Res, ProductForSaleDTO, Permission, ParsedBarcodeProductDTO, ColorDTO, SizeDTO } from 'core/api';
import * as SalesStore from '../SalesStore';
import { Command, Cordova, beep } from 'pos/common';
import { CatalogueFacade } from 'pos/facades';
import { Button, Col, Cols } from 'core/ui';
import { QRReader } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';

type State = {
	barcode: string;
};

class GetProductByBarcode extends PosScreenClassComponent<Modal.Actions<ParsedBarcodeProductDTO[]>, State> {

	createInitialState(_props: Modal.Actions<ParsedBarcodeProductDTO[]>): Nullable<State> {
		return { barcode: '' };
	}

	private form = React.createRef<UI.Form<State>>();
	private txtBarcode = React.createRef<UI.TextBox<State>>();

	protected getScreenId(): string {
		return 'SELL_BY_BARCODE';
	}

	protected getTitle(): string {
		return Res.Web.SELL_BY_BARCODE();
	}

	protected getImage(): string | SvgIcon {
		return Images.SearchBarcode;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOk });
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	onOk = async () => {

		const ok = await this.form.current!.updateModel();
		ok && await handleServerErrors(async () => {
			this.close(await parseBarcode(this.state.barcode, SalesStore.getTicket().Id));
		}, {
			onDomainError: async error => { await Message.show(error.message); }
		});

	};

	protected useKeypad = () => false;

	readQR = async (): Promise<void> => {
		const qrResult = await QRReader.read();

		if (!qrResult.ok)
			return;

		this.setState({ barcode: qrResult.value }, async () => {
			await this.form.current!.remountAll();
			await this.onOk();
		});

	};

	protected getContent(): React.ReactNode {

		const { Form, TextBox } = UI.controlsFor<State>();

		const scanQRButton = Cordova.isCordovaNavigator()
			? <Button buttonStyle='info' className='m-l-sm' onClick={this.readQR}><i className='fa fa-barcode m-r-sm m-l-sm' /></Button>
			: null;

		return <Form small ref={this.form} model={this.state}>
			<DialogIcon src={Images.SearchBarcode} />
			<Cols>
				<Col width='*' className='m-t-sm'>
					<TextBox ref={this.txtBarcode} linkTo={x => x.barcode} placeholder={Res.Web.SELL_BY_BARCODE_PLACEHOLDER()} />
				</Col>
			</Cols>
			<Cols>
				<Col width='*' className='m-t-sm text-center'>
					{scanQRButton}
				</Col>
			</Cols>
		</Form>;
	}

	onComponentDidMount(): void {
		this.txtBarcode.current!.activate();
	}

	onComponentWillUnmount(): void {
	}
}

async function parseBarcode(barcode: string, ticketId: number): Promise<ParsedBarcodeProductDTO[]> {

	async function canReadScaleTicketAgain(): Promise<boolean> {

		if (!SessionProvider.hasPermission(Permission.ScaleTicket_ReadAgain)) {
			await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.ScaleTicket_ReadAgain)));
			return false;
		}

		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_READ_SCALE_TICKET_AGAIN());
		return result.ok;
	}

	const result = await CatalogueFacade.parseBarcode(barcode, ticketId);

	if (result.IsScaleTicketBarcode && result.ScaleTicketWasAlreadyRead && !(await canReadScaleTicketAgain()))
		return [];

	return result.Products;
}


declare global {
	interface CommandType {
		sellProductByManualBarcode: (compose: (product: ProductForSaleDTO, quantity?: number) => Promise<Result>) => Promise<Result>;
		sellByBarcode: (barcode: string, compose: (product: ProductForSaleDTO) => Promise<Result>) => Promise<Result>;
	}
}

Command.register('sellProductByManualBarcode', Preconditions.ensure(Permission.Ticket_ComposeProduct), async (compose: (product: ProductForSaleDTO, quantity?: number, size?: SizeDTO, color?: ColorDTO) => Promise<Result>): Promise<Result> => {
	const parsedProducts = await Modal.show(GetProductByBarcode);
	if (!parsedProducts.ok)
		return Result.error();

	for (const product of parsedProducts.value) {
		const result = await compose(product.Product, product.Quantity ?? undefined, product.Size, product.Color);
		if (!result.ok)
			return result;
	}

	return Result.ok();
});

Command.register('sellByBarcode', 'NO_PRECONDITION', async (barcode: string, compose: (product: ProductForSaleDTO, quantity?: number, size?: SizeDTO, color?: ColorDTO) => Promise<Result>): Promise<Result> => {

	let parsedProducts: ParsedBarcodeProductDTO[] = [];
	const result = await handleServerErrors(async () => parsedProducts = await parseBarcode(barcode, SalesStore.getTicket().Id), {
		onDomainError: async error => {
			beep();
			await Message.show(error.message);
		}
	});

	if (!result.ok)
		return Result.error();

	for (const product of parsedProducts) {
		const result = await compose(product.Product, product.Quantity ?? undefined, product.Size, product.Color);
		if (!result.ok)
			return result;
	}

	return Result.ok();

});

